import numeral from "numeral";
import CountUp from "react-countup";

import {
  isBondsProject,
  isSharesProject,
  type Project,
} from "@tudigo-monorepo/core-tudigo-api-models";
import {
  formatCurrency,
  getCurrencySymbol,
} from "@tudigo-monorepo/core-tudigo-utils";
import { useGetSimulatorByProject } from "@tudigo-monorepo/web-tudigo-api-client";
import { useComparator } from "@tudigo-monorepo/web-tudigo-comparator";
import { Skeleton } from "@tudigo-monorepo/web-tudigo-components";

import { getProjectComparatorSimatorQuery } from "../../../../utils/projects-comparator-simulator.utils";
import { ProjectsComparatorSimulatorRepaymentsModal } from "./projects-comparator-simulator-repayments-modal";

export type ProjectsComparatorSimulatorRoiCardProps = {
  amountInCents: number;
  project: Project;
};

export function ProjectsComparatorSimulatorRoiCard(
  props: ProjectsComparatorSimulatorRoiCardProps,
) {
  const { simulatorVisibility } = props.project.marketingSettings ?? {};

  const { isLoading: isLoadingComparator } = useComparator();

  const { data: simulatorData, isFetching: isLoadingSimulator } =
    useGetSimulatorByProject({
      projectId: props.project?.id,
      query: getProjectComparatorSimatorQuery(props),
    });

  if (isLoadingComparator || isLoadingSimulator) {
    return (
      <div className="mb-6 mt-4 flex flex-col gap-y-2">
        <Skeleton className="min-h-[174px] rounded-lg" />
      </div>
    );
  }

  if (simulatorVisibility === "hidden") {
    return (
      <div className="mb-6 mt-4 grid min-h-[174px] place-items-center rounded-lg border border-neutral-200 bg-neutral-50 p-4">
        <p className="body300-bold text-black">
          Simulateur non disponible sur cette levée
        </p>
      </div>
    );
  }

  return (
    <div className="mb-6 mt-4 flex flex-1 flex-col gap-y-2">
      <article className="font-roobert bg-success-light border-success-medium flex h-full min-h-[174px] flex-col gap-y-6 rounded-lg border p-4">
        <header className="flex flex-col items-center gap-y-1 font-semibold">
          <p className="text-black-primary text-sm">
            Retour sur investissement
          </p>
          <p className="text-success text-lg">
            <CountUp
              start={props.amountInCents}
              end={
                simulatorData?.roiInCents !== undefined
                  ? Number(simulatorData?.roiInCents) / 100
                  : 0
              }
              duration={0.5}
              suffix={` ${getCurrencySymbol()}`}
              separator=" "
            />
          </p>
        </header>

        <ul className="flex flex-col gap-y-2 px-2">
          {simulatorData?.roiYield && simulatorData?.annualRoiYield && (
            <li className="flex w-full items-center justify-between">
              <span className="text-xs text-neutral-900">
                Rendement potentiel
              </span>
              <span className="text-black-primary text-xs font-semibold">
                {`x${simulatorData.roiYield} (soit ${numeral(
                  simulatorData.annualRoiYield,
                ).format("0%")} par an)`}
              </span>
            </li>
          )}
          {props.project.riskInPercent !== null && (
            <li className="flex w-full items-center justify-between">
              <span className="text-xs text-neutral-900">Risque</span>
              <span className="text-black-primary text-xs font-semibold">
                {props.project.riskInPercent / 10}/10
              </span>
            </li>
          )}
          {isSharesProject(props.project) &&
            props.project.taxExemptionMechanics &&
            props.project.taxExemptionMechanics?.length > 0 && (
              <li className="flex w-full items-center justify-between">
                <span className="text-xs text-neutral-900">
                  Économie fiscale potentielle
                </span>
                <span className="text-black-primary text-xs font-semibold">
                  {formatCurrency(Number(simulatorData?.taxExemptionInCents))}
                </span>
              </li>
            )}
          {isBondsProject(props.project) &&
            simulatorData?.repayments &&
            simulatorData.repayments.length > 0 && (
              <ProjectsComparatorSimulatorRepaymentsModal
                simulatorData={simulatorData}
                project={props.project}
              />
            )}
        </ul>
      </article>

      <p className="font-roobert text-black-primary text-center text-[10px]">
        Simulation hors frais Tudigo et brut d&apos;impôts
      </p>
    </div>
  );
}
